import {
  Box,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { StatsViewModel } from "./StatsViewModel";
import { provideGetAllStatsUseCase } from "../../di/UseCasesModule";
import { LoadingView } from "../../component/LoadingView";
import { HeaderView } from "../../component/HeaderView";
import {
  Assessment,
  School,
  SportsEsports,
  StarRate,
  TaskAlt,
} from "@mui/icons-material";
import { AllStats } from "../../../domain/model/Stats";
import { CustomTextView } from "../../component/CustomTextView";
import { COLOR_LIGHT_GRAY } from "../../../data/colors/Colors";

export function StatsView() {
  const viewModel = StatsViewModel(provideGetAllStatsUseCase());
  return (
    <Stack>
      <HeaderView
        title={"All Stats"}
        isLayoutFullWidthInSmallerScreens
        layout={<></>}
      />
      <Stack>
        {viewModel.loading ? (
          <LoadingView />
        ) : (
          <>
            {viewModel.allStats && <StatsDashboard data={viewModel.allStats} />}
          </>
        )}
      </Stack>
    </Stack>
  );
}
const difficultyOrder: Record<string, number> = {
  easy: 1,
  medium: 2,
  hard: 3,
};

function StatsDashboard({ data }: { data: AllStats }) {
  // Helper to color-code difficulty levels
  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty.toLowerCase()) {
      case "easy":
        return "success";
      case "medium":
        return "warning";
      case "hard":
        return "error";
      default:
        return "default";
    }
  };

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <Grid container spacing={3}>
        {/* --- ROW 1: HIGH LEVEL METRICS --- */}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Total Games"
            value={data.games.total}
            icon={<SportsEsports color="primary" fontSize="large" />}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Completed"
            value={data.games.completed}
            icon={<TaskAlt color="success" fontSize="large" />}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Avg Rating"
            value={`${data.ratings.average} / 5`}
            subtitle={`${data.ratings.total} total reviews`}
            icon={<StarRate sx={{ color: "#FFD700" }} fontSize="large" />}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Total Correct"
            value={data.questions.reduce(
              (sum, q) => sum + q.correctAnswersCount,
              0,
            )}
            icon={<Assessment color="secondary" fontSize="large" />}
          />
        </Grid>

        {/* --- ROW 2: DETAILED BREAKDOWNS --- */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 6, height: "100%" }}>
            <Box display="flex" alignItems="center" mb={2}>
              <School sx={{ mr: 1, color: "text.secondary" }} />
              <Typography variant="h6" fontWeight="medium">
                Games by School Type
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Box
              display="flex"
              justifyContent="space-around"
              textAlign="center"
              mt={4}
            >
              <Box>
                <Typography variant="h3" color="primary.main" fontWeight="bold">
                  {data.games.primary}
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                  Primary School
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="h3"
                  color="secondary.main"
                  fontWeight="bold"
                >
                  {data.games.secondary}
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                  Middle & High School
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 6, height: "100%" }}>
            <Box display="flex" alignItems="center" mb={2}>
              <Assessment sx={{ mr: 1, color: "text.secondary" }} />
              <Typography variant="h6" fontWeight="medium">
                Correct Answers by Difficulty
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />

            <Box display="flex" flexDirection="column" gap={2} mt={2}>
              {data.questions
                .sort((a, b) => {
                  // Get the weight based on the string (lowercased for safety), default to 99 if unknown
                  const weightA =
                    difficultyOrder[a.difficulty.toLowerCase()] || 99;
                  const weightB =
                    difficultyOrder[b.difficulty.toLowerCase()] || 99;
                  return weightA - weightB;
                })
                .map((q, index) => (
                  <Box
                    key={index}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box display="flex" alignItems="center" gap={2}>
                      <Chip
                        label={q.difficulty}
                        color={getDifficultyColor(q.difficulty) as any}
                        size="small"
                        sx={{ width: 80, fontWeight: "bold" }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        {q.mode.replace(/([A-Z])/g, " $1").trim()}{" "}
                        {/* Formats "PrimarySchool" to "Primary School" */}
                      </Typography>
                    </Box>
                    <Stack alignItems={"end"}>
                      <Typography variant="h6" fontWeight="bold">
                        {q.correctAnswersCount}{" "}
                        <Typography
                          component="span"
                          variant="body2"
                          color="text.secondary"
                        >
                          correct
                        </Typography>
                      </Typography>
                      <Stack
                        direction={"row"}
                        alignItems={"center"}
                        spacing={0.5}
                      >
                        <CustomTextView
                          text="accuracy:"
                          fontSize={8}
                          textColor={COLOR_LIGHT_GRAY}
                        />
                        <CustomTextView
                          text={`${q.accuracyPercentage}%`}
                          fontSize={12}
                          fontWeight="bold"
                        />
                      </Stack>
                    </Stack>
                  </Box>
                ))}
              {data.questions.length === 0 && (
                <Typography color="text.secondary" align="center" mt={2}>
                  No questions answered yet.
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

// Reusable mini-component for the top metric cards
function StatCard({
  title,
  value,
  subtitle,
  icon,
}: {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
}) {
  return (
    <Card elevation={2} sx={{ height: "100%", borderRadius: 6 }}>
      <CardContent>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Box>
            <Typography
              color="text.secondary"
              variant="subtitle2"
              gutterBottom
              fontWeight="bold"
              textTransform="uppercase"
            >
              {title}
            </Typography>
            <Typography
              variant="h4"
              component="div"
              fontWeight="bold"
              color="text.primary"
            >
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              p: 1,
              backgroundColor: "action.hover",
              borderRadius: "50%",
              display: "flex",
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}
